import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { SiteNav } from "@/components/site-nav";
import { HomePopup } from "@/components/home-popup";
import { Reveal } from "@/components/reveal";
import { SiteFooter } from "@/components/site-footer";
import { supabase } from "@/integrations/supabase/client";
import { CursoFlipCard } from "@/components/curso-flip-card";
import { LinkedInFeed } from "@/components/linkedin-feed";
import { LazyInView } from "@/components/lazy-in-view";

import { getAttribution } from "@/lib/utm-capture";
import { submitLead } from "@/lib/submit-lead.functions";
import { useFormTracking } from "@/lib/form-tracking";

import acciona from "@/assets/clientes/acciona.png.asset.json";
import anglo from "@/assets/clientes/anglo.png.asset.json";
import codelco from "@/assets/clientes/codelco.png.asset.json";
import comtecsa from "@/assets/clientes/comtecsa.png.asset.json";
import geovita from "@/assets/clientes/geovita.png.asset.json";
import sigma from "@/assets/clientes/sigma.png.asset.json";
import techint from "@/assets/clientes/techint.png.asset.json";
import acreditados from "@/assets/clientes/acreditados-vp-codelco.png.asset.json";
import otecCodelco from "@/assets/otec-acreditada-codelco.png.asset.json";
import cursosPersonas from "@/assets/cursos-personas.webp.asset.json";
import cursosEmpresas from "@/assets/cursos-empresas.webp.asset.json";
import cursosSence from "@/assets/sence-cursos.webp.asset.json";
import bukMineros from "@/assets/buk-mineros.jpg.asset.json";
import bukLogo from "@/assets/buk-logo.png.asset.json";
import bukLogoFull from "@/assets/buk-logo-full.png.asset.json";

import brandShapes from "@/assets/brand-shapes.png";
import bannerAnimado from "@/assets/banner-animado.mp4.asset.json";
import mine1 from "@/assets/mining/mine-1.webp";
import mine2 from "@/assets/mining/mine-2.webp";
import mine3 from "@/assets/mining/mine-3.webp";
import mine4 from "@/assets/mining/mine-4.webp";
import mine5 from "@/assets/mining/mine-5.webp";
import mine6 from "@/assets/mining/mine-6.webp";




export const Route = createFileRoute("/home")({
  head: () => ({
    meta: [
      { title: "Mine Class | Ecosistema Minero 4.0 y Capacitación Industrial" },
      {
        name: "description",
        content:
          "Mine Class ofrece capacitación, acreditación digital y tecnología propietaria (LMS, FaceID, SonIA, Mine Pass, VigIA) para la industria minera moderna.",
      },
      { property: "og:title", content: "Mine Class | Ecosistema Minero 4.0" },
      {
        property: "og:description",
        content:
          "Plataforma integral de capacitación minera con LMS, biometría, IA y trazabilidad digital para operaciones seguras y eficientes.",
      },
      { property: "og:url", content: "https://mine-class.com/home" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Mine Class | Ecosistema Minero 4.0" },
      { name: "twitter:description", content: "Plataforma integral de capacitación minera con LMS, biometría, IA y trazabilidad digital." },
    ],
    links: [{ rel: "canonical", href: "https://mine-class.com/home" }],
  }),
  component: HomePage,
});

type HeroSlide = {
  tag: string | null;
  title: string;
  text: string;
  cta: string;
  ctaHref: string | null;
  img: string;
  titleSize?: string | null;
  durationMs?: number | null;
};

const HERO_TITLE_SIZES: Record<string, string> = {
  sm: "text-2xl md:text-4xl",
  md: "text-3xl md:text-5xl",
  lg: "text-display-lg",
};


type PodcastEpisode = {
  id: string;
  title: string;
  subtitle: string | null;
  spotify_embed_html: string | null;
  youtube_embed_html: string | null;
};

const DEFAULT_HERO_SLIDES: HeroSlide[] = [];

const isVideoUrl = (u: string) => /\.(mp4|webm|ogg|mov|m4v)(\?|#|$)/i.test(u ?? "");


const STATS = [
  { v: "+30M", l: "USD de ahorro al año para nuestros clientes" },
  { v: "500k", l: "Horas de capacitación ejecutadas" },
  { v: "200k", l: "Mineros capacitados desde 2021" },
  { v: "+500", l: "Estudiantes capacitados por día" },
];
const CLIENTS = [
  { name: "Codelco", src: codelco.url },
  { name: "AngloAmerican", src: anglo.url },
  { name: "Acciona", src: acciona.url },
  { name: "Techint", src: techint.url },
  { name: "Geovita", src: geovita.url },
  { name: "Sigma", src: sigma.url },
  { name: "Comtecsa", src: comtecsa.url },
  { name: "OTEC Acreditada por Codelco", src: acreditados.url },
];
const MINELABS = [
  { img: mine2, title: "LMS Mine Class", desc: "Plataforma de gestión de aprendizaje diseñada para entornos industriales de alta exigencia.", href: "/soluciones/tecnologia-aprendizaje/athena-lms" },
  { img: mine5, title: "FaceID", desc: "Validación biométrica avanzada para control de asistencia y acceso a capacitación crítica.", href: "/soluciones/tecnologia-aprendizaje/face-id" },
  { img: mine6, title: "SonIA", desc: "Inteligencia Artificial aplicada al análisis de competencias y feedback predictivo.", href: "/soluciones/tecnologia-aprendizaje/sonia" },
  { img: mine1, title: "Cursos y Diplomados", desc: "Catálogo extenso de formación técnica acreditada con certificación industrial.", href: "/cursos/empresas" },
  { img: mine3, title: "Mine Pass", desc: "El pasaporte digital de capacitación para el ingreso ágil a faenas mineras.", href: "/soluciones/acreditacion-contratistas/mine-pass" },
  { img: mine4, title: "Examine", desc: "Sistema de evaluación riguroso con supervisión remota y análisis de resultados.", href: "/soluciones/acreditacion-contratistas/examine" },
];



const PRESS = [
  { source: "Reporte Minero", title: "Mine Class lanza Mine Pass: pasaporte digital con IA para acreditación", text: "Nueva solución tecnológica para optimizar los procesos de ingreso a faena mediante inteligencia artificial." },
  { source: "Portal Minero", title: "Mine Class lanza V Radiografía de la Capacitación Minera para medir escenario en Chile y Perú", text: "El estudio busca identificar brechas en la formación y oportunidades de mejora en el sector minero regional." },
  { source: "Prensa Industrial", title: "Innovación en Acreditación: El impacto de la IA en la minería moderna", text: "Cómo las nuevas tecnologías están reduciendo tiempos de espera y aumentando la seguridad operativa." },
  { source: "Minería Digital", title: "Resultados de la V Radiografía revelan la necesidad de mayor capacitación en IA", text: "Análisis detallado sobre las competencias digitales requeridas por la fuerza laboral minera actual." },
];

function HomePage() {
  const statsVideoRef = useRef<HTMLVideoElement>(null);
  const [slide, setSlide] = useState(0);
  const [slides, setSlides] = useState<HeroSlide[]>(DEFAULT_HERO_SLIDES);
  const [noticias, setNoticias] = useState<{ slug: string; title: string; excerpt: string | null; category: string | null; og_image_url: string | null; published_at: string | null }[]>([]);
  const [podcastEp, setPodcastEp] = useState<PodcastEpisode | null>(null);

  useEffect(() => {
    supabase
      .from("hero_slides")
      .select("tag,title,subtitle,image_url,cta_label,cta_href,title_size,duration_ms")
      .eq("is_active", true)
      .order("sort_order")
      .then(({ data }) => {
        if (data && data.length > 0) {
          setSlides(
            data.map((r) => ({
              tag: r.tag,
              title: r.title,
              text: r.subtitle ?? "",
              cta: r.cta_label ?? "Ver más",
              ctaHref: r.cta_href,
              img: r.image_url,
              titleSize: (r as { title_size?: string | null }).title_size ?? "lg",
              durationMs: (r as { duration_ms?: number | null }).duration_ms ?? 9000,
            })),
          );
        }
      });

    supabase
      .from("noticias")
      .select("slug,title,excerpt,category,og_image_url,published_at")
      .eq("is_published", true)
      .order("published_at", { ascending: false })
      .limit(4)
      .then(({ data }) => {
        if (data) setNoticias(data);
      });

    supabase
      .from("podcast_episodes" as never)
      .select("id,title,subtitle,spotify_embed_html,youtube_embed_html")
      .eq("is_published", true)
      .order("episode_number", { ascending: false, nullsFirst: false })
      .limit(1)
      .then(({ data }) => {
        if (data && data.length > 0) setPodcastEp(data[0] as PodcastEpisode);
      });
  }, []);

  useEffect(() => {
    if (slides.length < 2) return;
    const delay = Math.max(6000, slides[slide]?.durationMs ?? 9000);
    const t = setTimeout(() => setSlide((s) => (s + 1) % slides.length), delay);
    return () => clearTimeout(t);
  }, [slides, slide]);

  return (
    <div className="bg-white text-[var(--color-on-surface)] overflow-x-hidden">
      <SiteNav />
      <HomePopup />

      <h1 className="sr-only">Mine Class — Ecosistema Minero 4.0</h1>

      {/* HERO */}
      <header className="relative w-full h-[520px] md:h-[700px] overflow-hidden bg-[var(--color-brand-purple)]">
        {slides.map((s, i) => (
          <div key={i} className={`carousel-slide${i === slide ? " active" : ""}`}>
            {!(i === slide || i === (slide + 1) % slides.length || i === 0) ? null : isVideoUrl(s.img) ? (
              <video
                src={s.img}
                aria-hidden="true"
                autoPlay
                loop
                muted
                playsInline
                preload={i === slide ? "auto" : "none"}
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              <img
                src={s.img}
                alt=""
                aria-hidden="true"
                loading={i === 0 ? "eager" : "lazy"}
                fetchPriority={i === 0 ? "high" : "low"}
                decoding={i === 0 ? "sync" : "async"}
                className="parallax-bg absolute inset-0 w-full h-full object-cover"
              />
            )}
            <div className="absolute inset-0 bg-[var(--color-brand-purple)]/60 mix-blend-multiply" />
            <div className="relative z-20 h-full max-w-container-max mx-auto px-margin-desktop flex items-center">
              <div className="max-w-2xl text-white">
                {s.tag && <span className="inline-block py-1 px-3 bg-[var(--color-brand-coral)] mb-4 md:mb-6 font-mono-data text-[10px] md:text-xs tracking-widest uppercase">{s.tag}</span>}
                <h2 className={`${HERO_TITLE_SIZES[s.titleSize ?? "lg"] ?? HERO_TITLE_SIZES.lg} font-extrabold mb-4 md:mb-6 leading-tight break-words hyphens-auto [overflow-wrap:anywhere]`} lang="es">{s.title}</h2>
                {s.text && <p className="text-base md:text-lg mb-6 md:mb-10 text-white/80 max-w-lg">{s.text}</p>}
                <div className="flex gap-4 flex-wrap">
                  {s.ctaHref ? (
                    <a href={s.ctaHref} className="px-6 md:px-8 py-3 md:py-4 bg-[var(--color-brand-coral)] text-white font-bold hover:scale-105 hover-glow transition-all flex items-center gap-2 group text-sm md:text-base">
                      {s.cta} <span className="material-symbols-outlined group-hover:translate-x-2 transition-transform">arrow_forward</span>
                    </a>
                  ) : (
                    <button className="px-6 md:px-8 py-3 md:py-4 bg-[var(--color-brand-coral)] text-white font-bold hover:scale-105 hover-glow transition-all flex items-center gap-2 group text-sm md:text-base">
                      {s.cta} <span className="material-symbols-outlined group-hover:translate-x-2 transition-transform">arrow_forward</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
        {slides.length > 1 && (
          <>
            <button
              type="button"
              aria-label="Slide anterior"
              onClick={() => setSlide((s) => (s - 1 + slides.length) % slides.length)}
              className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-30 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-black/35 hover:bg-[var(--color-brand-coral)] text-white transition-colors backdrop-blur-sm"
            >
              <span className="material-symbols-outlined">chevron_left</span>
            </button>
            <button
              type="button"
              aria-label="Slide siguiente"
              onClick={() => setSlide((s) => (s + 1) % slides.length)}
              className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-30 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-black/35 hover:bg-[var(--color-brand-coral)] text-white transition-colors backdrop-blur-sm"
            >
              <span className="material-symbols-outlined">chevron_right</span>
            </button>
          </>
        )}
        <div className="absolute bottom-12 right-8 flex gap-4 z-20">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Ir al slide ${i + 1}`}
              aria-current={i === slide ? "true" : undefined}
              onClick={() => setSlide(i)}
              className={`w-12 h-1 transition-all ${i === slide ? "bg-[var(--color-brand-coral)]" : "bg-white/30 hover:bg-white/60"}`}
            />
          ))}
        </div>
      </header>

      {/* STATS */}
      <Reveal as="section" className="bg-[#6b7280] relative overflow-hidden">
        <div
          className="stats-banner group relative w-full mx-auto"
          style={{ aspectRatio: "1476 / 280" }}
          onMouseEnter={() => {
            if (statsVideoRef.current) statsVideoRef.current.pause();
          }}
          onMouseLeave={() => {
            if (statsVideoRef.current) statsVideoRef.current.play();
          }}
        >
          {/* Video curtain — always playing, pauses on hover */}
          <video
            ref={statsVideoRef}
            className="stats-banner__video absolute inset-0 w-full h-full object-cover"
            src={bannerAnimado.url}
            autoPlay
            loop
            muted
            playsInline
            preload="none"
          />
          <div className="absolute inset-0 bg-[#6b7280]/30 pointer-events-none" />

          {/* Stats overlay — normal by default, shrink on hover */}
          <div className="stats-banner__grid absolute inset-0 z-10 grid grid-cols-2 md:grid-cols-4 items-center px-6 md:px-12 gap-4">
            {STATS.map((s) => (
              <div key={s.l} className="stats-banner__item text-white text-center md:text-left">
                <div className="stats-banner__value font-mono-data font-bold leading-none tracking-tight">{s.v}</div>
                <div className="stats-banner__label font-bold uppercase tracking-widest text-white/90">{s.l}</div>
              </div>
            ))}
          </div>

          {/* CTA — only on hover */}
          <Link
            to="/contacto/reunion"
            className="stats-banner__cta absolute inset-0 z-20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          >
            <span className="inline-flex items-center gap-3 px-6 md:px-8 py-3 md:py-4 bg-[var(--color-brand-purple)] text-white font-bold uppercase tracking-widest text-xs md:text-sm hover:gap-4 transition-all rounded-full shadow-lg">
              Agenda una reunión con nosotros hoy
              <span className="material-symbols-outlined">arrow_forward</span>
            </span>
          </Link>
        </div>
      </Reveal>



      {/* CLIENTS TICKER */}
      <Reveal as="section" className="py-12 bg-white overflow-hidden border-b border-[var(--color-outline-variant)]">
        <h3 className="text-center text-sm text-[var(--color-on-surface-variant)] uppercase tracking-widest opacity-50 mb-6">Confían en nosotros</h3>
        <div className="flex whitespace-nowrap ticker-scroll">
          {[0, 1].map((k) => (
            <div key={k} className="flex items-center gap-24 mx-12">
              {CLIENTS.map((c) => (
                <div key={c.name + k} className="h-20 w-40 shrink-0 flex items-center justify-center">
                  <img
                    src={c.src}
                    alt={c.name}
                    title={c.name}
                    loading="lazy"
                    className="max-h-full max-w-full object-contain transition-all duration-300"
                    style={{ filter: "grayscale(1)", opacity: 0.75 }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.filter = "grayscale(0)";
                      e.currentTarget.style.opacity = "1";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.filter = "grayscale(1)";
                      e.currentTarget.style.opacity = "0.75";
                    }}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      </Reveal>

      {/* CURSOS CODELCO VP */}
      <Reveal as="section" className="py-xl bg-[var(--color-surface-container-low)] border-b border-[var(--color-outline-variant)]">
        <div className="max-w-container-max mx-auto px-margin-desktop">
          <div className="grid md:grid-cols-[auto_1fr] gap-8 md:gap-12 items-center">
            <div className="flex justify-center md:justify-start">
              <img
                src={otecCodelco.url}
                alt="OTEC acreditada por Codelco"
                className="w-40 h-40 md:w-48 md:h-48 object-contain"
                loading="lazy"
              />
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-[var(--color-brand-purple)] mb-3">
                Cursos Codelco VP
              </h2>
              <p className="text-[var(--color-on-surface-variant)] text-base md:text-lg leading-relaxed mb-5">
                Somos una Otec acreditada por Codelco para realizar los cursos estandarizados para la
                Vicepresidencia de Proyectos de Codelco. Coordinamos y realizamos las inducciones
                para habilitar a los trabajadores, permitiendo el ingreso de manera más rápida a
                Faena. Las inducciones las realizamos de manera virtual por Zoom con relatores con
                vasta experiencia en el rubro.
              </p>
              <ul className="space-y-2 mb-6">
                {[
                  "Certificados inmediatos",
                  "Apoyo en el proceso de inducción",
                  "Coordinación de cursos en menos de 24 hrs",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm md:text-base">
                    <span className="material-symbols-outlined text-[var(--color-brand-coral)] mt-0.5">check_circle</span>
                    <span className="text-[var(--color-on-surface)]">{item}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/cursos/empresas"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[var(--color-brand-coral)] text-white font-bold text-sm uppercase tracking-wider rounded-md hover:brightness-110 transition-all"
              >
                Conoce más
                <span className="material-symbols-outlined text-base">arrow_forward</span>
              </Link>
            </div>
          </div>
        </div>
      </Reveal>

      {/* NOVEDAD: CURSO DS 44 */}
      <Reveal as="section" className="bg-white border-b border-[var(--color-outline-variant)] pt-14 md:pt-20">
        <div className="max-w-container-max mx-auto px-margin-desktop py-6">
          <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-8">
            <p className="flex flex-wrap items-center gap-x-3 gap-y-2 text-lg md:text-xl font-semibold text-[var(--color-brand-purple)]">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-brand-coral)] px-3 py-1 text-xs font-bold uppercase tracking-wider text-[var(--color-brand-coral)]">
                <span className="material-symbols-outlined text-base">auto_awesome</span>
                Nuevo
              </span>
              Nueva Normativa en Prevención de Riesgo Decreto 44
              <span className="text-base font-normal text-[var(--color-on-surface-variant)]">· 100% online</span>
            </p>
            <a
              href="https://umayor.mine-class.com/programa/d8e1b8aa-8f74-4362-b0d4-f65603ad53b4"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md bg-[var(--color-brand-coral)] px-6 py-2.5 text-sm font-bold uppercase tracking-wider text-white transition-all hover:brightness-110"
            >
              Conoce más
              <span className="material-symbols-outlined text-lg">arrow_forward</span>
            </a>
          </div>
        </div>
      </Reveal>

      {/* CURSOS */}
      <Reveal as="section" className="py-xl bg-white">
        <div className="max-w-container-max mx-auto px-margin-desktop">
          <div className="mb-12">
            <h2 className="text-headline-lg text-[var(--color-brand-purple)] mb-2">Cursos y Diplomados</h2>
            <p className="text-[var(--color-on-surface-variant)] max-w-xl">
              Formación técnica especializada para profesionales de la industria minera.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-gutter">
            <CursoFlipCard
              title="Cursos para Personas"
              description="Cursos abiertos para estudiantes, particulares y empresas en temas destacados de la industria y actualidad minera."
              to="/cursos/personas"
              titleClassName="text-4xl"
              imageRatio="70-30"
              imageUrl={cursosPersonas.url}
              footer="En alianza con la Universidad Mayor"
            />
            <CursoFlipCard
              title="Cursos para Empresas"
              description="Cursos para empresas de alto nivel técnico, temas adaptados a solicitud de las compañías, con los más destacados relatores."
              to="/cursos/empresas"
              titleClassName="text-4xl"
              imageRatio="70-30"
              imageUrl={cursosEmpresas.url}
            />
            <CursoFlipCard
              title="Cursos SENCE"
              description="Cursos gratuitos de minería y transformación digital en alianza con SENCE para impulsar tu desarrollo profesional."
              to="/cursos/personas/sence"
              titleClassName="text-4xl"
              imageRatio="70-30"
              imageUrl={cursosSence.url}
              footer="Alianza SENCE"
            />
            <CursoFlipCard
              title="Descuentos Buk"
              description="Beneficio exclusivo para clientes Buk: hasta 30% de descuento en nuestros cursos y certificaciones para equipos mineros."
              to="/cursos/empresas/buk"
              titleClassName="text-4xl"
              imageRatio="70-30"
              imageUrl={bukLogoFull.url}
              footer="Beneficio clientes Buk"
            />
          </div>

        </div>

      </Reveal>





      {/* MINELABS */}
      <Reveal as="section" className="py-xl bg-[var(--color-surface-container-low)] relative overflow-hidden">

        <div className="max-w-container-max mx-auto px-margin-desktop relative z-10">
          <div className="mb-12">
            <h2 className="text-headline-lg text-[var(--color-brand-purple)] mb-2">Encuentra tu solución</h2>
            <p className="text-[var(--color-on-surface-variant)] max-w-xl">Conoce todos los productos que Mine Class tiene para ti. </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-gutter">
            {MINELABS.map((m) => (
              <Link
                key={m.title}
                to={m.href}
                className="relative overflow-hidden border border-[var(--color-outline-variant)] hover-card card-tilt group bg-[var(--color-brand-purple)] min-h-[320px]"
              >
                <img
                  src={m.img}
                  alt={m.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-40 group-hover:scale-105 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-brand-purple)] via-[var(--color-brand-purple)]/70 to-transparent" />
                <div className="relative z-10 p-8 flex flex-col h-full min-h-[320px] justify-end text-white">
                  <h3 className="text-headline-md mb-3">{m.title}</h3>
                  <p className="text-white/80 mb-6 text-sm">{m.desc}</p>
                  <span className="flex items-center gap-2 text-[var(--color-brand-coral)] font-bold group-hover:gap-4 transition-all self-start">
                    Saber más <span className="material-symbols-outlined">arrow_forward</span>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </Reveal>






      {/* LINKEDIN */}
      <LinkedInFeed />

      {/* PODCAST + PRENSA */}
      <Reveal as="section" className="py-xl bg-white">
        <div className="max-w-container-max mx-auto px-margin-desktop grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <div className="flex items-center gap-3 mb-8">
              <span className="material-symbols-outlined text-[var(--color-brand-coral)] text-4xl animate-pulse" style={{ fontSize: 36 }}>podcasts</span>
              <h2 className="text-headline-lg text-[var(--color-brand-purple)]">Historias que Escalan</h2>
            </div>
            {podcastEp ? (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-[var(--color-brand-purple)]">{podcastEp.title}</h3>
                  {podcastEp.subtitle && <p className="text-sm text-[var(--color-on-surface-variant)] mt-1">{podcastEp.subtitle}</p>}
                </div>
                {podcastEp.spotify_embed_html && (
                  <LazyInView className="rounded-xl overflow-hidden shadow-lg border border-[var(--color-outline-variant)] hover-card bg-[var(--color-surface-container-low)] aspect-video">
                    <div
                      className="w-full h-full [&_iframe]:w-full [&_iframe]:h-full [&_iframe]:border-0"
                      dangerouslySetInnerHTML={{ __html: podcastEp.spotify_embed_html }}
                    />
                  </LazyInView>
                )}
                {podcastEp.youtube_embed_html && (
                  <LazyInView className="rounded-xl overflow-hidden shadow-lg border border-[var(--color-outline-variant)] hover-card bg-[var(--color-surface-container-low)] aspect-video">
                    <div
                      className="w-full h-full [&_iframe]:w-full [&_iframe]:h-full [&_iframe]:border-0"
                      dangerouslySetInnerHTML={{ __html: podcastEp.youtube_embed_html }}
                    />
                  </LazyInView>
                )}
              </div>
            ) : (
              <div className="text-center py-16 border border-[var(--color-outline-variant)] bg-white">
                <p className="text-[var(--color-on-surface-variant)]">Próximamente nuevos episodios.</p>
              </div>
            )}
          </div>
          <div>
            <div className="flex items-center gap-3 mb-8">
              <span className="material-symbols-outlined text-[var(--color-brand-coral)] text-4xl" style={{ fontSize: 36 }}>newspaper</span>
              <h2 className="text-headline-lg text-[var(--color-brand-purple)]">Últimas Noticias</h2>
            </div>
            <div className="space-y-4">
              {noticias.length > 0
                ? noticias.map((n) => (
                    <Link
                      key={n.slug}
                      to="/comunicaciones/noticias/$slug"
                      params={{ slug: n.slug }}
                      className="p-6 border border-[var(--color-outline-variant)] hover-card group bg-white shadow-sm block"
                    >
                      {n.category && (
                        <div className="text-xs text-[var(--color-brand-coral)] font-bold mb-2 uppercase tracking-widest">
                          {n.category}
                        </div>
                      )}
                      <h4 className="font-bold text-lg mb-2 group-hover:text-[var(--color-brand-coral)] transition-colors">{n.title}</h4>
                      {n.excerpt && (
                        <p className="text-sm text-[var(--color-on-surface-variant)] line-clamp-2">{n.excerpt}</p>
                      )}
                    </Link>
                  ))
                : PRESS.map((p) => (
                    <div key={p.title} className="p-6 border border-[var(--color-outline-variant)] hover-card cursor-pointer group bg-white shadow-sm">
                      <div className="text-xs text-[var(--color-brand-coral)] font-bold mb-2 uppercase tracking-widest">{p.source}</div>
                      <h4 className="font-bold text-lg mb-2 group-hover:text-[var(--color-brand-coral)] transition-colors">{p.title}</h4>
                      <p className="text-sm text-[var(--color-on-surface-variant)]">{p.text}</p>
                    </div>
                  ))}
              {noticias.length > 0 && (
                <Link to="/comunicaciones/noticias" className="inline-flex items-center gap-2 text-[var(--color-brand-coral)] font-bold mt-4">
                  Ver todas las noticias <span className="material-symbols-outlined">arrow_forward</span>
                </Link>
              )}
            </div>
          </div>

        </div>
      </Reveal>

      {/* CTA / FORM */}
      <Reveal as="section" className="py-xl bg-[var(--color-brand-purple)] text-white relative overflow-hidden">
        <div className="relative z-10 max-w-container-max mx-auto px-margin-desktop">
          <div className="text-center mb-16">
            <h2 className="text-display-lg mb-4">¿Listo para transformar tu operación?</h2>
            <p className="text-white/70 max-w-2xl mx-auto">Conversa con nuestros especialistas y descubre cómo nuestra tecnología puede optimizar tus procesos y seguridad.</p>
          </div>
          <div className="bg-white/5 backdrop-blur-md p-10 border border-white/10 hover:border-[var(--color-brand-coral)]/50 transition-colors mx-auto max-w-2xl">
            <h3 className="text-headline-md mb-8 flex items-center gap-3">
              <span className="material-symbols-outlined text-[var(--color-brand-coral)]" style={{ fontSize: 32 }}>rocket_launch</span> ¿En qué te ayudamos?
            </h3>
            <HomeContactForm />
          </div>
        </div>
      </Reveal>


      <SiteFooter />

    </div>
  );
}

const HOME_PRODUCTOS = ["Athena", "Mine Pass", "FaceID", "SonIA", "VigIA", "Zeus", "Cursos y Diplomados", "Examine"];
const HOME_PAISES = ["Chile", "Perú", "Colombia", "México", "Argentina", "Otro"];

function HomeContactForm() {
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const { trackStart, trackComplete } = useFormTracking("home-demo");


  const inputCls = "bg-white/10 border border-white/20 focus:border-[var(--color-brand-coral)] text-white placeholder-white/50 px-4 py-3 w-full outline-none";
  const selectCls = "bg-white/10 border border-white/20 focus:border-[var(--color-brand-coral)] text-white px-4 py-3 w-full outline-none [&_option]:text-[var(--color-on-surface)] [&_option]:bg-[var(--color-outline-variant)]";

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const fd = new FormData(e.currentTarget);
      const payload = {
        first_name: String(fd.get("first_name") ?? "").trim(),
        last_name: String(fd.get("last_name") ?? "").trim(),
        email: String(fd.get("email") ?? "").trim(),
        phone: String(fd.get("phone") ?? "").trim() || null,
        company: String(fd.get("company") ?? "").trim() || null,
        job_title: String(fd.get("job_title") ?? "").trim() || null,
        country: String(fd.get("country") ?? "").trim() || null,
        employees: Number(fd.get("employees")) || null,
        interest: String(fd.get("interest") ?? "").trim() || null,
        message: String(fd.get("message") ?? "").trim() || null,
        source: "form" as const,
      };
      // Un solo envío al servidor: panel → Google Sheet → CRM (en ese orden).
      const { first, last } = getAttribution();
      const qs = new URLSearchParams(window.location.search);
      const result = await submitLead({
        data: {
          ...payload,
          attribution: { first, last },
          user_agent: navigator.userAgent,
          language: navigator.language,
          utm_source: qs.get("utm_source") ?? "",
          utm_medium: qs.get("utm_medium") ?? "",
          utm_campaign: qs.get("utm_campaign") ?? "",
          pagina_origen: window.location.href,
        },
      });
      if (!result.ok) throw new Error("Error enviando el formulario.");

      (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag?.("event", "generate_lead", { form_id: "home" });
      trackComplete();

      setOk(true);
      (e.target as HTMLFormElement).reset();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Error enviando el formulario.");
    } finally {
      setBusy(false);
    }
  }

  if (ok) {
    return (
      <div
        className="text-center py-10 animate-in fade-in zoom-in duration-300"
        role="status"
        aria-live="polite"
      >
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[var(--color-brand-coral)]/20 text-[var(--color-brand-coral)] mb-4">
          <span className="material-symbols-outlined" style={{ fontSize: 32 }} aria-hidden="true">check_circle</span>
        </div>
        <h4 className="text-2xl font-bold text-white">¡Recibimos tu solicitud!</h4>
        <p className="text-white/80 mt-2 max-w-md mx-auto">Te contactaremos dentro de las próximas 24 horas hábiles.</p>
        <button type="button" onClick={() => setOk(false)} className="mt-6 px-6 py-3 bg-[var(--color-brand-coral)] text-white font-bold uppercase tracking-widest hover:brightness-110 transition-all">Enviar otra</button>
      </div>
    );
  }

  return (
    <form
      className="space-y-4"
      onSubmit={submit}
      onFocusCapture={trackStart}
      aria-label="Solicitud de contacto para empresas"

      aria-busy={busy}
      aria-describedby={err ? "home-form-error" : undefined}
    >
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="hf-first-name" className="sr-only">Nombre</label>
          <input id="hf-first-name" name="first_name" autoComplete="given-name" aria-required="true" className={inputCls} placeholder="Nombre*" required />
        </div>
        <div>
          <label htmlFor="hf-last-name" className="sr-only">Apellido</label>
          <input id="hf-last-name" name="last_name" autoComplete="family-name" aria-required="true" className={inputCls} placeholder="Apellido*" required />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="hf-email" className="sr-only">Correo electrónico</label>
          <input id="hf-email" name="email" type="email" autoComplete="email" inputMode="email" aria-required="true" aria-invalid={!!err} className={inputCls} placeholder="Correo*" required />
        </div>
        <div>
          <label htmlFor="hf-phone" className="sr-only">Número de teléfono</label>
          <input id="hf-phone" name="phone" type="tel" autoComplete="tel" inputMode="tel" aria-required="true" className={inputCls} placeholder="Número de teléfono*" required />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="hf-company" className="sr-only">Empresa</label>
          <input id="hf-company" name="company" autoComplete="organization" aria-required="true" className={inputCls} placeholder="Empresa*" required />
        </div>
        <div>
          <label htmlFor="hf-country" className="sr-only">País o región</label>
          <select id="hf-country" name="country" autoComplete="country-name" defaultValue="" aria-required="true" required className={selectCls}>
            <option value="" disabled>País/Región*</option>
            {HOME_PAISES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="hf-job" className="sr-only">Cargo</label>
          <input id="hf-job" name="job_title" autoComplete="organization-title" aria-required="true" className={inputCls} placeholder="Cargo*" required />
        </div>
        <div>
          <label htmlFor="hf-employees" className="sr-only">Número de empleados</label>
          <input id="hf-employees" name="employees" type="number" inputMode="numeric" min={1} aria-required="true" className={inputCls} placeholder="N° de empleados*" required />
        </div>
      </div>
      <div>
        <label htmlFor="hf-interest" className="sr-only">Producto Mine Class de interés</label>
        <select id="hf-interest" name="interest" defaultValue="" aria-required="true" required className={selectCls}>
          <option value="" disabled>Producto Mine Class de interés*</option>
          {HOME_PRODUCTOS.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>
      <div>
        <label htmlFor="hf-message" className="sr-only">Mensaje</label>
        <textarea id="hf-message" name="message" rows={3} className={inputCls} placeholder="Déjanos tu mensaje" />
      </div>
      <div aria-live="assertive" aria-atomic="true" className="min-h-[1.25rem]">
        {err && <p id="home-form-error" role="alert" className="text-sm text-red-300">{err}</p>}
      </div>
      <button type="submit" disabled={busy} aria-disabled={busy} className="w-full py-4 bg-[var(--color-brand-coral)] text-white font-bold hover:brightness-110 hover-glow transition-all uppercase tracking-widest disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2">
        {busy ? (
          <>
            <span className="inline-block w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" aria-hidden="true" />
            Enviando...
          </>
        ) : (
          "Enviar Solicitud"
        )}
      </button>
    </form>
  );
}

